import { colors } from '@/constants';
import { useCart, useClient, useCorporateFlags } from '@/hooks';
import { packingTypes } from '@/shared';
import { PackagesTypesProps, PackagingProductProps, ProductProps } from '@/types';
import { useMemo } from 'react';
import * as S from './styles';

interface Product {
  size?: number;
  showQtd?: boolean;
  product: ProductProps;
  pack: PackagesTypesProps;
  packQty: number;
  setPackage: (val: PackagesTypesProps) => void;
  setPackQuantity: (val: number) => void;
  autoSelectFirst?: boolean;
  canDisablePack?: boolean;
  fullBorder?: boolean;
}

const RenderPackaging = ({
  pack,
  size,
  product,
  packQty,
  setPackage,
  setPackQuantity,
  canDisablePack = true,
  fullBorder = false,
}: Product) => {
  const { getLimit } = useCart();
  const { selectedClient } = useClient();
  const { selectedBrandPair } = useCorporateFlags();
  const toNumber = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const getSegmentCode = (segmento: any) => {
    return toNumber(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento);
  };
  const selectedClientScope = useMemo(() => {
    if (!selectedClient) return selectedClient;
    if (!selectedBrandPair?.codLoja || !selectedBrandPair?.codSegmento) return selectedClient;

    const lojas = Array.isArray(selectedClient.lojas) ? selectedClient.lojas : [];
    const matchedLoja = lojas.find((loja: any) => {
      if (toNumber(loja?.COD_LOJA) !== Number(selectedBrandPair.codLoja)) return false;
      const segmentosLoja = Array.isArray(loja?.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
      return segmentosLoja.some((segmento: any) => getSegmentCode(segmento) === Number(selectedBrandPair.codSegmento));
    });
    if (!matchedLoja) return selectedClient;

    const segmentosLoja = Array.isArray(matchedLoja?.COD_SEGMENTO) ? matchedLoja.COD_SEGMENTO : [];
    const segmentoAtivo = segmentosLoja.find((segmento: any) => getSegmentCode(segmento) === Number(selectedBrandPair.codSegmento));

    return {
      ...selectedClient,
      ...matchedLoja,
      COD_SEGMENTO: segmentoAtivo ? [segmentoAtivo] : segmentosLoja,
    };
  }, [selectedClient, selectedBrandPair]);
  const clientSegs = (Array.isArray(selectedClientScope?.COD_SEGMENTO) ? selectedClientScope.COD_SEGMENTO : [])
    .map((segmento: any) => getSegmentCode(segmento))
    .filter((segmento): segmento is number => segmento != null);
  const clientLoja = toNumber(selectedClientScope?.COD_LOJA);

  const list = useMemo(() => {
    const embs = (product?.EMBALAGEM ? [...product.EMBALAGEM] : []).filter((emb) => {
      const embLoja = toNumber(emb?.COD_LOJA);
      const embSegmento = toNumber(emb?.COD_SEGMENTO);

      const lojaOk = clientLoja == null || embLoja == null || embLoja === clientLoja;
      if (!lojaOk) return false;

      if (!clientSegs.length) return true;
      if (embSegmento == null) return false;
      return clientSegs.includes(embSegmento);
    });
    
    if (!embs.length) return (product?.EMBALAGEM || []).slice(0, 1);

    const orderedList = embs.sort((a, b) => {
      const aQtd = (a.QTD_MULTIPLO_VENDA || 1) * (a.QTD_EMBALAGEM || 1);
      const bQty = (b.QTD_MULTIPLO_VENDA || 1) * (b.QTD_EMBALAGEM || 1);
      return aQtd < bQty ? 1 : -1;
    });
    return orderedList;
  }, [product.EMBALAGEM, clientLoja, clientSegs]) as PackagingProductProps[];

  const limit = getLimit(product?.COD_PRODUTO);

  return (
    <>
      <S.Row>
        {/* Rever esse map */}
        {list.map((embalagem) => {
          const packing = packingTypes?.[embalagem?.SG_EMBALAGEM];

          const qty = embalagem?.SG_EMBALAGEM === 'PK' ? embalagem?.QTD_MULTIPLO_VENDA : embalagem?.QTD_EMBALAGEM;
          if (!packing || !qty) return null;

          const isActive =
            pack === 'PK'
              ? embalagem?.QTD_MULTIPLO_VENDA > 1 && embalagem?.QTD_MULTIPLO_VENDA === packQty && pack === embalagem?.SG_EMBALAGEM
              : embalagem?.SG_EMBALAGEM === pack && embalagem?.QTD_EMBALAGEM === packQty;
          const rawPrecos: any = product?.PRECO || {};
          const list: any[] = Array.isArray(rawPrecos) ? rawPrecos : Object.values(rawPrecos || {});
          const hasPrice = list.find((p) => {
            if (p?.SG_EMBALAGEM !== packing?.shortName) return false;
            const sameQty = packing?.shortName === 'PK' ? Number(p?.QTD_MULTIPLO_VENDA) === Number(qty) : Number(p?.QTD_EMBALAGEM) === Number(qty);
            if (!sameQty) return false;
            const priceLoja = toNumber(p?.COD_LOJA);
            return clientLoja == null || priceLoja == null || priceLoja === clientLoja;
          });
          const isDisabled = canDisablePack && (limit < qty || !hasPrice);

          return (
            <S.BoxEmbalagem
              size={size}
              $fullBorder={fullBorder}
              $isactive={isActive}
              $unityPack={list.length === 1}
              onClick={() => {
                if (isDisabled) return;

                setPackage(packing?.shortName);
                setPackQuantity(qty);
              }}
              key={`pack-${product.COD_PRODUTO}-${packing?.shortName}-${qty}`}
              data-test={`pack-${product.COD_PRODUTO}-${packing?.shortName}-${qty}`}
            >
              <S.PackImage src={packing?.icon} alt="" />
              {isActive && <S.CheckFill size={24} color={colors.alert.green110} />}

              <S.BoxLabel>
                <S.BoxQtdEmbalagem>
                  <S.QtdEmbalagem>{qty}/un</S.QtdEmbalagem>
                </S.BoxQtdEmbalagem>
              </S.BoxLabel>
            </S.BoxEmbalagem>
          );
        })}
      </S.Row>
    </>
  );
};

export default RenderPackaging;
